import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  Chip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControlLabel,
  Alert,
  CircularProgress,
  IconButton,
  Menu,
  Divider,
  SelectChangeEvent
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import GetAppIcon from '@mui/icons-material/GetApp';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useSafeBack } from '../hooks/useSafeBack';
import { toast } from 'react-toastify';
import axios from 'axios';
import { getAuthHeader } from '../services/authService';

interface FilterState {
  flagLevel: string;
  organizationId: string;
  teamId: string;
  fromDate: Date | null;
  toDate: Date | null;
  patientIds: string[];
  surveyIds: string[];
  includeInactive: boolean;
  consented: boolean;
}

interface Organization {
  id: number;
  name: string;
}

interface Team {
  id: number;
  name: string;
  organizationId: number;
}

interface Patient {
  id: number;
  firstName: string;
  lastName: string;
  schoolId: string;
  email: string;
  active: boolean;
}

interface Survey {
  id: number;
  name: string;
  description: string;
}

interface SurveyResult {
  id: number;
  patientId: number;
  patientFirstName: string;
  patientLastName: string;
  patientSchoolId: string;
  surveyId: number;
  surveyName: string;
  completedAt: string;
  totalScore: number;
  flagLevel: 'RED' | 'YELLOW' | 'GREEN';
  questions: {
    id: number;
    question: string;
    answer: string;
    points: number;
  }[];
}

const ProviderResultsPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<FilterState>({
    flagLevel: '',
    organizationId: '',
    teamId: '',
    fromDate: null,
    toDate: null,
    patientIds: [],
    surveyIds: [],
    includeInactive: false,
    consented: false
  });
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [results, setResults] = useState<SurveyResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedResults, setExpandedResults] = useState<number[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedResult, setSelectedResult] = useState<SurveyResult | null>(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (filters.organizationId) {
      fetchTeams(filters.organizationId);
    } else {
      setTeams([]);
      setFilters(prev => ({ ...prev, teamId: '' }));
    }
  }, [filters.organizationId]);

  useEffect(() => {
    if (filters.teamId) {
      fetchPatients(filters.teamId);
    } else {
      setPatients([]);
      setFilters(prev => ({ ...prev, patientIds: [] }));
    }
  }, [filters.teamId]);

  const fetchInitialData = async () => {
    try {
      const [orgsResponse, surveysResponse] = await Promise.all([
        axios.get(`/api/provider/organizations`, { headers: getAuthHeader() }),
        axios.get(`/api/surveys`, { headers: getAuthHeader() })
      ]);
      setOrganizations(orgsResponse.data);
      setSurveys(surveysResponse.data);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast.error('Failed to load data');
    }
  };

  const fetchTeams = async (organizationId: string) => {
    try {
      const response = await axios.get(
        `/api/teams?organizationId=${organizationId}`,
        { headers: getAuthHeader() }
      );
      setTeams(response.data);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const fetchPatients = async (teamId: string) => {
    try {
      const response = await axios.get(
        `/api/students?teamId=${teamId}&includeInactive=${filters.includeInactive}`,
        { headers: getAuthHeader() }
      );
      setPatients(response.data);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const handleFilterChange = (field: keyof FilterState) => (
    event: SelectChangeEvent<any> | React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFilters(prev => ({
      ...prev,
      [field]: Array.isArray(value) ? value : value
    }));
  };

  const handleDateChange = (field: 'fromDate' | 'toDate') => (date: Date | null) => {
    setFilters(prev => ({
      ...prev,
      [field]: date
    }));
  };

  const handleCheckboxChange = (field: 'includeInactive' | 'consented') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleViewResults = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.flagLevel) params.append('flagLevel', filters.flagLevel);
      if (filters.organizationId) params.append('organizationId', filters.organizationId);
      if (filters.teamId) params.append('teamId', filters.teamId);
      if (filters.fromDate) params.append('fromDate', filters.fromDate.toISOString());
      if (filters.toDate) params.append('toDate', filters.toDate.toISOString());
      if (filters.patientIds.length) params.append('patientIds', filters.patientIds.join(','));
      if (filters.surveyIds.length) params.append('surveyIds', filters.surveyIds.join(','));
      if (filters.consented) params.append('consented', 'true');

      const response = await axios.get(
        `/api/provider/survey-results?${params.toString()}`,
        { headers: getAuthHeader() }
      );
      setResults(response.data);
    } catch (error) {
      console.error('Error fetching results:', error);
      toast.error('Failed to fetch survey results');
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.flagLevel) params.append('flagLevel', filters.flagLevel);
      if (filters.organizationId) params.append('organizationId', filters.organizationId);
      if (filters.teamId) params.append('teamId', filters.teamId);
      if (filters.fromDate) params.append('fromDate', filters.fromDate.toISOString());
      if (filters.toDate) params.append('toDate', filters.toDate.toISOString());
      if (filters.patientIds.length) params.append('patientIds', filters.patientIds.join(','));
      if (filters.surveyIds.length) params.append('surveyIds', filters.surveyIds.join(','));
      if (filters.consented) params.append('consented', 'true');

      const response = await axios.get(
        `/api/provider/survey-results/export?${params.toString()}`,
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `survey_results_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Data exported successfully');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    }
  };

  const toggleResultExpansion = (resultId: number) => {
    setExpandedResults(prev => 
      prev.includes(resultId) 
        ? prev.filter(id => id !== resultId)
        : [...prev, resultId]
    );
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, result: SurveyResult) => {
    setAnchorEl(event.currentTarget);
    setSelectedResult(result);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedResult(null);
  };

  const handleLogout = () => {
    logout();
  };

  const goBack = useSafeBack();

  const getRiskColor = (flagLevel: string) => {
    switch (flagLevel) {
      case 'GREEN': return 'success';
      case 'YELLOW': return 'warning';
      case 'RED': return 'error';
      default: return 'default';
    }
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Psychology Profile System - Survey Results
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {user?.firstName?.charAt(0) || 'U'}
            </Avatar>
            <Typography variant="body1">
              {user?.firstName} {user?.lastName}
            </Typography>
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Button variant="outlined" onClick={goBack}>
            ← Back to Dashboard
          </Button>
          <Typography variant="h4">
            Filter Survey Results
          </Typography>
        </Box>

        {/* Filter Panel */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FilterListIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Filters</Typography>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Flag Level</InputLabel>
                  <Select
                    value={filters.flagLevel}
                    onChange={handleFilterChange('flagLevel')}
                    label="Flag Level"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="RED">Red Flag</MenuItem>
                    <MenuItem value="YELLOW">Yellow Flag</MenuItem>
                    <MenuItem value="GREEN">Green Flag</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Organization</InputLabel>
                  <Select
                    value={filters.organizationId}
                    onChange={handleFilterChange('organizationId')}
                    label="Organization"
                  >
                    <MenuItem value="">Select Organization</MenuItem>
                    {organizations.map(org => (
                      <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth disabled={!filters.organizationId}>
                  <InputLabel>Team/Group</InputLabel>
                  <Select
                    value={filters.teamId}
                    onChange={handleFilterChange('teamId')}
                    label="Team/Group"
                  >
                    <MenuItem value="">Select Team</MenuItem>
                    {teams.map(team => (
                      <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="From Date"
                    value={filters.fromDate}
                    onChange={handleDateChange('fromDate')}
                    slotProps={{
                      textField: {
                        fullWidth: true
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="To Date"
                    value={filters.toDate}
                    onChange={handleDateChange('toDate')}
                    slotProps={{
                      textField: {
                        fullWidth: true
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!filters.teamId}>
                  <InputLabel>Select Patients</InputLabel>
                  <Select
                    multiple
                    value={filters.patientIds}
                    onChange={handleFilterChange('patientIds')}
                    label="Select Patients"
                    renderValue={(selected) => 
                      `${(selected as string[]).length} patient(s) selected`
                    }
                  >
                    {patients.map(patient => (
                      <MenuItem key={patient.id} value={patient.id}>
                        <Checkbox checked={filters.patientIds.includes(patient.id.toString())} />
                        <ListItemText 
                          primary={`${patient.lastName}, ${patient.firstName}`}
                          secondary={`ID: ${patient.schoolId}`}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Select Surveys</InputLabel>
                  <Select
                    multiple
                    value={filters.surveyIds}
                    onChange={handleFilterChange('surveyIds')}
                    label="Select Surveys"
                    renderValue={(selected) => 
                      `${(selected as string[]).length} survey(s) selected`
                    }
                  >
                    {surveys.map(survey => (
                      <MenuItem key={survey.id} value={survey.id}>
                        <Checkbox checked={filters.surveyIds.includes(survey.id.toString())} />
                        <ListItemText primary={survey.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.includeInactive}
                      onChange={handleCheckboxChange('includeInactive')}
                    />
                  }
                  label="Include Inactive Accounts"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.consented}
                      onChange={handleCheckboxChange('consented')}
                    />
                  }
                  label="Consented Only"
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleViewResults}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                  >
                    View Results
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleExportData}
                    startIcon={<GetAppIcon />}
                  >
                    Export Data
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Results Display */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : results.length > 0 ? (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {results.length} Result(s) Found
            </Typography>
            {results.map((result) => (
              <Accordion
                key={result.id}
                expanded={expandedResults.includes(result.id)}
                onChange={() => toggleResultExpansion(result.id)}
                sx={{ mb: 2 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography>
                        {result.patientLastName}, {result.patientFirstName} ({result.patientSchoolId}) | 
                        {' '}{new Date(result.completedAt).toLocaleString()} | 
                        {' '}{result.surveyName}
                      </Typography>
                    </Box>
                    <Chip 
                      label={`${result.totalScore} - ${result.flagLevel} Flag`}
                      color={getRiskColor(result.flagLevel) as any}
                      size="small"
                      sx={{ ml: 2 }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOpen(e, result);
                      }}
                      sx={{ ml: 1 }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Alert severity={
                      result.flagLevel === 'RED' ? 'error' : 
                      result.flagLevel === 'YELLOW' ? 'warning' : 
                      'success'
                    } sx={{ mb: 2 }}>
                      Patient is in {result.flagLevel} FLAG area
                    </Alert>
                    
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Total Score: {result.totalScore}
                    </Typography>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ mb: 2 }}>
                      <Button
                        variant="contained"
                        onClick={() => navigate(`/provider/patients/${result.patientId}/assessments/${result.surveyId}/results`)}
                      >
                        View Detailed Results
                      </Button>
                    </Box>
                    
                    {result.questions.map((question, index) => (
                      <Box key={question.id} sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                          {index + 1}. {question.question}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>Answer:</strong> {question.answer}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          <strong>Points:</strong> {question.points}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6" color="textSecondary">
              No results found
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Adjust your filters and click "View Results" to see survey data
            </Typography>
          </Box>
        )}

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => {
            handleMenuClose();
            if (selectedResult) {
              navigate(`/provider/patients/${selectedResult.patientId}/assessments/${selectedResult.surveyId}/results`);
            }
          }}>
            View Detailed Results
          </MenuItem>
          <MenuItem onClick={() => {
            handleMenuClose();
            // TODO: Implement reassign survey
            toast.info('Reassign survey functionality coming soon');
          }}>
            Reassign Survey
          </MenuItem>
          <MenuItem onClick={() => {
            handleMenuClose();
            // TODO: Implement follow-up notes
            toast.info('Follow-up notes functionality coming soon');
          }}>
            Add Follow-up Note
          </MenuItem>
        </Menu>
      </Container>
    </>
  );
};

export default ProviderResultsPage;